import { usePageSEO } from "@/lib/use-page-seo";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useTranslation, Trans } from "react-i18next";
import i18n from "@/i18n";
import { useLocalePath } from "@/lib/use-locale-path";
import WCLogo from "@/components/WCLogo";
import LiveBadge from "@/components/LiveBadge";
import TeamGrid from "@/components/TeamGrid";
import HowItWorks from "@/components/HowItWorks";
import FAQSection from "@/components/FAQSection";
import ObfuscatedEmail from "@/components/ObfuscatedEmail";
import MegaFooter from "@/components/MegaFooter";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLocalPrice } from "../hooks/useLocalPrice";

type Team = {
  id: number;
  name: string;
  shortName: string;
  crestUrl: string;
};

// Session type pills
const SESSION_TYPES = [
  { key: "Practice", labelKey: "sessions.practice" },
  { key: "Qualifying", labelKey: "sessions.qualifying" },
  { key: "Sprint Qualifying", labelKey: "sessions.sprint_qualifying" },
  { key: "Sprint", labelKey: "sessions.sprint" },
  { key: "Race", labelKey: "sessions.race" },
] as const;

// Session types available per series (keyed by TheSportsDB league ID)
const SERIES_SESSION_TYPES: Record<number, readonly string[]> = {
  4370: ["Practice", "Qualifying", "Sprint Qualifying", "Sprint", "Race"], // F1
  4486: ["Practice", "Qualifying", "Sprint", "Race"],                      // F2
  4487: ["Practice", "Qualifying", "Sprint", "Race"],                      // F3
  4371: ["Practice", "Qualifying", "Race"],                                // Formula E
  4373: ["Practice", "Qualifying", "Race"],                                // IndyCar
};

const DEFAULT_SESSIONS = new Set(["Race"]);

type SignupStatus = "idle" | "submitting" | "sent" | "login_sent" | "error";

export default function HomePage() {
  usePageSEO("meta.title", "/");
  const { t } = useTranslation();
  const lp = useLocalePath();
  const { bracket: localEstimate } = useLocalPrice(1.99, "USD");

  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set(DEFAULT_SESSIONS));
  const [email, setEmail] = useState("");
  const [signupStatus, setSignupStatus] = useState<SignupStatus>("idle");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSignIn, setShowSignIn] = useState(false);
  const [signInEmail, setSignInEmail] = useState("");
  const { toast } = useToast();

  const { data: teams = [], isPending: teamsLoading } = useQuery<Team[]>({
    queryKey: ["/api/teams"],
  });

  const filteredTeams = useMemo(() => {
    if (!searchQuery.trim()) return teams;
    const q = searchQuery.toLowerCase();
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.shortName.toLowerCase().includes(q)
    );
  }, [teams, searchQuery]);

  // Compute which session types are available based on selected series
  const availableSessionTypes = useMemo(() => {
    if (selectedTeams.length === 0) {
      return new Set(SESSION_TYPES.map((st) => st.key));
    }
    const union = new Set<string>();
    for (const team of selectedTeams) {
      const sessions = SERIES_SESSION_TYPES[team.id];
      if (sessions) {
        for (const s of sessions) union.add(s);
      }
    }
    return union;
  }, [selectedTeams]);

  // Remove stale session selections when available types change
  useEffect(() => {
    setSelectedSessions((prev) => {
      const cleaned = new Set<string>();
      for (const s of prev) {
        if (availableSessionTypes.has(s)) cleaned.add(s);
      }
      if (cleaned.size !== prev.size) return cleaned;
      return prev;
    });
  }, [availableSessionTypes]);

  const toggleTeam = (team: Team) => {
    setSelectedTeams((prev) => {
      const exists = prev.find((t) => t.id === team.id);
      if (exists) return prev.filter((t) => t.id !== team.id);
      if (!exists) (window as any).posthog?.capture('team_selected', { team: team.name, code: team.shortName });
      return [...prev, team];
    });
  };

  const toggleSession = (key: string) => {
    setSelectedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const signupMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/signup", {
        email: email.trim(),
        teamIds: selectedTeams.map((t) => t.id),
        sessionTypes: Array.from(selectedSessions),
        locale: i18n.language,
      });
    },
    onSuccess: async (res) => {
      const data = await res.json();
      if (data.status === "verification_sent") {
        setSignupStatus("sent");
        (window as any).posthog?.capture('signup_submitted', { teams: selectedTeams.length, is_multi_team: selectedTeams.length >= 2, sessions: Array.from(selectedSessions) });
      } else if (data.status === "login_sent") {
        setSignupStatus("login_sent");
        (window as any).posthog?.capture('login_requested');
      }
    },
    onError: async (err: any) => {
      setSignupStatus("error");
      toast({
        title: t("errors.something_went_wrong"),
        description: err.message ?? t("errors.please_try_again"),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || selectedTeams.length === 0) return;
    setSignupStatus("submitting");
    signupMutation.mutate();
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInEmail.trim()) return;
    setSignupStatus("submitting");
    try {
      const res = await apiRequest("POST", "/api/signup", {
        email: signInEmail.trim(),
        teamIds: [],
        locale: i18n.language,
      });
      const data = await res.json();
      if (data.status === "login_sent") {
        setSignupStatus("login_sent");
        setEmail(signInEmail);
      } else if (data.status === "verification_sent") {
        setSignupStatus("sent");
        setEmail(signInEmail);
      } else {
        setSignupStatus("error");
        toast({ title: t("errors.something_went_wrong"), description: t("errors.please_try_again"), variant: "destructive" });
      }
    } catch (err: any) {
      setSignupStatus("idle");
      const msg = err?.message ?? "";
      if (msg.includes("not_found") || msg.includes("404")) {
        toast({ title: t("errors.no_account_found"), description: t("errors.no_account_description"), variant: "destructive" });
      } else {
        toast({ title: t("errors.something_went_wrong"), description: t("errors.please_try_again"), variant: "destructive" });
      }
    }
  };

  const isMultiTeam = selectedTeams.length >= 2;
  const teamCount = selectedTeams.length;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-border/40 backdrop-blur-md bg-background/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <nav className="flex items-center gap-4 text-sm text-muted-foreground">
            <a
              href="#how-it-works"
              onClick={(e) => { e.preventDefault(); document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" }); }}
              className="hover:text-foreground transition-colors cursor-pointer hidden sm:block"
            >
              {t("nav.how_it_works")}
            </a>
            <a
              href="#faq"
              onClick={(e) => { e.preventDefault(); document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }); }}
              className="hover:text-foreground transition-colors cursor-pointer hidden sm:block"
            >
              {t("nav.faq")}
            </a>
            <Badge
              variant="outline"
              className="border-accent/60 text-accent text-xs font-medium px-2 py-0.5"
              data-testid="live-badge-nav"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-accent live-pulse inline-block me-1.5" />
              {t("nav.live_updates")}
            </Badge>
            <LanguageSwitcher />
          </nav>
        </div>
      </header>

      <main>
        {/* ── Hero ────────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden">
          {/* Hero background — photo */}
          <div className="absolute inset-0">
            <img src="/hero-f1.jpg" alt="" className="w-full h-full object-cover" loading="eager" fetchPriority="high" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-background" />
          </div>

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-8 sm:pt-14 sm:pb-10">
            <div className="max-w-2xl">
              {/* Live auto-update hero element */}
              <div className="animate-fade-in-up mb-3">
                <LiveBadge />
              </div>

              {/* Hero headline — two equal-weight lines */}
              <h1
                className="animate-fade-in-up-delay-1 font-display text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-[1.1] mb-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                data-testid="hero-heading"
              >
                {t("hero.title_line1")}
                <br />
                <span className="text-gold-gradient">{t("hero.title_line2")}</span>
              </h1>

              {/* Subheading */}
              <p
                className="animate-fade-in-up-delay-2 text-sm sm:text-base md:text-lg text-foreground/90 max-w-xl leading-relaxed mb-4"
                data-testid="hero-subheading"
              >
                {t("hero.subtitle")}
              </p>

              {/* Works with — app icons */}
              <div className="animate-fade-in-up-delay-3 flex flex-wrap items-center gap-5 mb-3">
                {[
                  { name: t("hero.works_with_apple"), src: "/apple-calendar.png", invert: true, size: 24 },
                  { name: t("hero.works_with_google"), src: "/google-calendar.svg", invert: false, size: 28 },
                  { name: t("hero.works_with_outlook"), src: "/outlook.svg", invert: false, size: 28 },
                ].map((app) => (
                  <div key={app.name} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <img
                      src={app.src}
                      alt={app.name}
                      width={app.size}
                      height={app.size}
                      className={app.invert ? "brightness-0 invert" : ""}
                      loading="lazy"
                    />
                    <span>{app.name}</span>
                  </div>
                ))}
              </div>

              {/* Value prop chips */}
              <div className="animate-fade-in-up-delay-3 flex flex-wrap gap-2 mb-4">
                {[t("hero.chip_scores"), t("hero.chip_multi"), t("hero.chip_auto")].map((v) => (
                  <span
                    key={v}
                    className="text-xs font-medium px-3 py-1.5 rounded-full border border-border/60 text-muted-foreground bg-card/60"
                  >
                    {v}
                  </span>
                ))}
              </div>

              {/* CTA arrow pointing down */}
              <div className="animate-fade-in-up-delay-3 flex items-center gap-2 text-sm text-muted-foreground">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary animate-bounce">
                  <path d="M12 5v14M5 12l7 7 7-7"/>
                </svg>
                <span>{t("hero.cta_arrow")}</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Series picker + session pills + signup ────────────────────────── */}
        <section className="py-8 sm:py-10" id="pick-teams">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">

            {/* Section header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
              <div className="flex-1">
                <h2
                  className="text-3xl sm:text-4xl font-bold uppercase tracking-tight"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {t("teams.heading")}
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {t("teams.subheading", { localEstimate: localEstimate ? ` ${localEstimate}` : "" })}
                </p>
              </div>

              {/* Selected count */}
              {teamCount > 0 && (
                <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-4 py-2 text-sm" data-testid="selected-count">
                  <span className="text-muted-foreground">{t("teams.selected")}</span>
                  <span className="font-semibold text-foreground">{t(teamCount !== 1 ? "teams.team_count_plural" : "teams.team_count", { count: teamCount })}</span>
                  {isMultiTeam && (
                    <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-full">
                      {t("teams.once_off", { localEstimate: localEstimate ? ` ${localEstimate}` : "" })}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Series grid */}
            {teamsLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                <p className="text-sm text-muted-foreground">{t("teams.loading")}</p>
              </div>
            ) : (
              <TeamGrid
                teams={filteredTeams}
                selectedTeams={selectedTeams}
                onToggle={toggleTeam}
                searchQuery={searchQuery}
              />
            )}

            {/* ── Session type pills ──────────────────────────────────────────── */}
            <div className="mt-8" data-testid="session-pills">
                <h3
                  className="text-xl font-bold uppercase tracking-tight mb-3"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {t("sessions.heading")}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("sessions.subheading")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {SESSION_TYPES.filter((st) => availableSessionTypes.has(st.key)).map((st) => (
                    <button
                      key={st.key}
                      onClick={() => toggleSession(st.key)}
                      className={`
                        px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150 cursor-pointer
                        ${selectedSessions.has(st.key)
                          ? "team-chip-selected border-primary/70 bg-primary/10"
                          : "border-border/50 bg-card/60 hover:border-border text-muted-foreground hover:text-foreground"
                        }
                      `}
                      aria-pressed={selectedSessions.has(st.key)}
                      data-testid={`session-pill-${st.key}`}
                    >
                      {t(st.labelKey)}
                    </button>
                  ))}
                </div>
            </div>

            {/* Signup form + Sign-in (animated swap) */}
            {(signupStatus === "idle" || signupStatus === "submitting" || signupStatus === "error") ? (
              <div className="mt-10 max-w-xl" id="signup">
                {/* Signup box */}
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                    showSignIn ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"
                  }`}
                >
                  <div className="overflow-hidden">
                  <div className="rounded-xl border border-border/60 bg-card p-6 sm:p-8">
                    <h3
                      className="text-2xl font-bold uppercase mb-1"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      {isMultiTeam ? t("signup.multi_heading") : t("signup.free_heading")}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-5">
                      {isMultiTeam
                        ? t("signup.multi_description", { count: teamCount, localEstimate: localEstimate ? ` ${localEstimate}` : "" })
                        : t("signup.free_description")}
                    </p>

                    {/* Selected series chips */}
                    {selectedTeams.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-5" data-testid="selected-teams-list">
                        {selectedTeams.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => toggleTeam(s)}
                            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-primary/60 bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                            data-testid={`selected-team-${s.id}`}
                          >
                            {s.crestUrl && <img src={s.crestUrl} alt="" className="w-4 h-4 object-contain" />}
                            <span className="font-medium">{s.shortName}</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                          </button>
                        ))}
                      </div>
                    )}

                    {selectedTeams.length === 0 && (
                      <div className="mb-5 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
                        {t("signup.select_prompt")}
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder={t("signup.email_placeholder")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={signupStatus === "submitting"}
                        className="flex-1 bg-muted border-border/60 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary"
                        data-testid="email-input"
                      />
                      <Button
                        type="submit"
                        disabled={selectedTeams.length === 0 || !email.trim() || signupStatus === "submitting"}
                        className="bg-primary text-primary-foreground font-semibold shrink-0 hover-elevate"
                        data-testid="submit-btn"
                      >
                        {signupStatus === "submitting" ? (
                          <span className="flex items-center gap-2">
                            <svg className="w-4 h-4 sync-ring" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 12a9 9 0 11-6.219-8.56"/>
                            </svg>
                            {t("signup.submit_sending")}
                          </span>
                        ) : (
                          isMultiTeam ? t("signup.submit_multi") : t("signup.submit_free")
                        )}
                      </Button>
                    </form>

                    {isMultiTeam && (
                      <p className="text-xs text-muted-foreground mt-3">
                        {t("signup.payment_note", { localEstimate: localEstimate ? ` ${localEstimate}` : "" })}
                      </p>
                    )}
                  </div>

                  {/* "Already have an account?" link */}
                  <div className="mt-4 text-sm text-muted-foreground text-center">
                    {t("signup.already_have_account")}{" "}
                    <button
                      onClick={() => setShowSignIn(true)}
                      className="text-primary hover:underline cursor-pointer"
                      data-testid="show-signin-btn"
                    >
                      {t("signup.sign_in_link")}
                    </button>
                  </div>
                  </div>
                </div>

                {/* Sign-in box */}
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                    showSignIn ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                  <div className="rounded-xl border border-border/60 bg-card p-6 sm:p-8">
                    <h3
                      className="text-2xl font-bold uppercase mb-1"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      {t("signin.heading")}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-5">
                      {t("signin.description")}
                    </p>
                    <form onSubmit={handleSignIn} className="flex flex-col sm:flex-row gap-3">
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder={t("signup.email_placeholder")}
                        value={signInEmail}
                        onChange={(e) => setSignInEmail(e.target.value)}
                        required
                        disabled={signupStatus === "submitting"}
                        className="flex-1 bg-muted border-border/60 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary"
                        data-testid="signin-email-input"
                      />
                      <Button
                        type="submit"
                        disabled={!signInEmail.trim() || signupStatus === "submitting"}
                        className="bg-primary text-primary-foreground font-semibold shrink-0 hover-elevate"
                        data-testid="signin-btn"
                      >
                        {signupStatus === "submitting" ? t("signin.sending") : t("signin.submit")}
                      </Button>
                    </form>
                  </div>

                  <div className="mt-4 text-sm text-muted-foreground text-center">
                    <button
                      onClick={() => setShowSignIn(false)}
                      className="text-primary hover:underline cursor-pointer"
                    >
                      {t("signup.back_to_teams")}
                    </button>
                  </div>
                  </div>
                </div>
              </div>
            ) : signupStatus === "sent" ? (
              <div className="mt-10 max-w-xl animate-fade-in-up" data-testid="verification-sent">
                <div className="rounded-xl border border-accent/30 bg-accent/5 p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent">
                        <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4l-10 7L2 4"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{t("verification.check_inbox")}</h3>
                      <p className="text-sm text-muted-foreground mt-1" dangerouslySetInnerHTML={{ __html: t("verification.sent_to", { email }) }} />
                      <p className="text-xs text-muted-foreground mt-2">{t("verification.check_spam")}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : signupStatus === "login_sent" ? (
              <div className="mt-10 max-w-xl animate-fade-in-up" data-testid="login-sent">
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-6 sm:p-8">
                  <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t("verification.login_sent", { email }) }} />
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {/* ── How it works ───────────────────────────────────────────────────── */}
        <HowItWorks />

        {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
        <FAQSection />

        {/* ── Network Footer ───────────────────────────────────────────────── */}
        <MegaFooter />

        {/* ── Footer ──────────────────────────────────────────────────────────── */}
        <footer className="border-t border-border/40 py-10 mt-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <WCLogo compact />
              <span>{t("footer.copyright")}</span>
              <span className="hidden sm:inline">·</span>
              <span><Trans i18nKey="footer.skella" components={{ 1: <a href="https://skella.com.au/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors underline-offset-2 hover:underline" /> }} /></span>
            </div>
            <div className="flex items-center gap-5">
              <a href={lp("/guide")} className="hover:text-foreground transition-colors">{t("nav.guide")}</a>
              <a
                href="#faq"
                onClick={(e) => { e.preventDefault(); document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }); }}
                className="hover:text-foreground transition-colors cursor-pointer"
              >{t("nav.faq")}</a>
              <a href={lp("/faq")} className="hover:text-foreground transition-colors">{t("nav.privacy")}</a>
              <ObfuscatedEmail />
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
